import type {
  BookingComparable,
  PriceBounds,
  PricingFactor,
  SuggestedPricing,
  SuggestedPricingInput,
} from "./types";

const COUNTRY_CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  US: "$", GB: "£", IE: "€", FR: "€", DE: "€", ES: "€", IT: "€",
  NL: "€", BE: "€", AT: "€", PT: "€", FI: "€", GR: "€",
  AU: "A$", CA: "CA$", NZ: "NZ$", CH: "CHF", SE: "kr", NO: "kr", DK: "kr",
  AE: "AED", ZA: "R",
};

export function getCurrencySymbol(countryCode: string | null | undefined): string {
  if (!countryCode) return "$";
  return COUNTRY_CURRENCY_SYMBOLS[countryCode] || "$";
}

/**
 * Map ISO country code → ISO currency code. Used by
 * {@link computeSuggestedPricing} to look up the per-currency chauffeured
 * floor (folds in Task #1477's per-currency floor table).
 */
const COUNTRY_TO_CURRENCY: Readonly<Record<string, string>> = {
  US: "USD", GB: "GBP",
  IE: "EUR", FR: "EUR", DE: "EUR", ES: "EUR", IT: "EUR", NL: "EUR",
  BE: "EUR", AT: "EUR", PT: "EUR", FI: "EUR", GR: "EUR",
  AU: "AUD", CA: "CAD", NZ: "NZD", CH: "CHF",
  SE: "SEK", NO: "NOK", DK: "DKK", AE: "AED", ZA: "ZAR",
  PL: "PLN",
};

/**
 * Per-currency chauffeured hourly floor — a hard minimum below which a
 * chauffeured listing is rejected. The floor reflects the real-world cost
 * of a driver's labour in each market and is the **only** pricing minimum
 * that produces a host-facing error (self-drive and event have no hard
 * min). Folded in from Task #1477 — that task is superseded by #1540.
 *
 * Task #1770: do NOT change these without checking — the per-currency hard
 * floor is the only host-facing chauffeur error and downstream "resume into
 * pricing" flows depend on its exact behaviour.
 */
const CHAUFFEURED_HOURLY_FLOOR_BY_CURRENCY: Readonly<Record<string, number>> = {
  USD: 25, GBP: 20, EUR: 25, CAD: 30, AUD: 35, NZD: 30, CHF: 30,
  SEK: 220, NOK: 220, DKK: 175, AED: 90, ZAR: 350, PLN: 120,
};

const DEFAULT_CHAUFFEURED_HOURLY_FLOOR = 25;

/**
 * Per-currency delivery fee thresholds used by the listing wizard's
 * DeliverySection to decide when to show "seems high" / "seems low" warnings
 * and to populate market-appropriate input placeholders. The values reflect
 * typical host delivery rates observed per market; Scandinavian currencies
 * (DKK/SEK/NOK) are ~7–10× GBP nominal, so their thresholds are scaled
 * accordingly. The "low" floor is also scaled — 0.1 kr/km is functionally
 * free and meaningless for those markets.
 */
export interface DeliveryFeeThresholds {
  kmHigh: number;
  kmLow: number;
  kmTypicalMin: number;
  kmTypicalMax: number;
  mileHigh: number;
  mileLow: number;
  mileTypicalMin: number;
  mileTypicalMax: number;
  kmPlaceholder: string;
  milePlaceholder: string;
  minFeePlaceholder: string;
}

const DELIVERY_FEE_THRESHOLDS_BY_CURRENCY: Readonly<Record<string, DeliveryFeeThresholds>> = {
  GBP: { kmHigh: 5,  kmLow: 0.1, kmTypicalMin: 1,  kmTypicalMax: 5,  mileHigh: 8,   mileLow: 0.1, mileTypicalMin: 1,  mileTypicalMax: 8,   kmPlaceholder: "2.00", milePlaceholder: "3.00", minFeePlaceholder: "25"  },
  USD: { kmHigh: 5,  kmLow: 0.1, kmTypicalMin: 1,  kmTypicalMax: 5,  mileHigh: 8,   mileLow: 0.1, mileTypicalMin: 1,  mileTypicalMax: 8,   kmPlaceholder: "2",    milePlaceholder: "3",    minFeePlaceholder: "25"  },
  EUR: { kmHigh: 8,  kmLow: 0.1, kmTypicalMin: 1,  kmTypicalMax: 8,  mileHigh: 12,  mileLow: 0.1, mileTypicalMin: 1,  mileTypicalMax: 12,  kmPlaceholder: "2",    milePlaceholder: "3",    minFeePlaceholder: "25"  },
  AUD: { kmHigh: 10, kmLow: 0.1, kmTypicalMin: 2,  kmTypicalMax: 10, mileHigh: 15,  mileLow: 0.1, mileTypicalMin: 2,  mileTypicalMax: 15,  kmPlaceholder: "3",    milePlaceholder: "4",    minFeePlaceholder: "40"  },
  CAD: { kmHigh: 10, kmLow: 0.1, kmTypicalMin: 2,  kmTypicalMax: 10, mileHigh: 15,  mileLow: 0.1, mileTypicalMin: 2,  mileTypicalMax: 15,  kmPlaceholder: "3",    milePlaceholder: "4",    minFeePlaceholder: "40"  },
  NZD: { kmHigh: 10, kmLow: 0.1, kmTypicalMin: 2,  kmTypicalMax: 10, mileHigh: 15,  mileLow: 0.1, mileTypicalMin: 2,  mileTypicalMax: 15,  kmPlaceholder: "3",    milePlaceholder: "4",    minFeePlaceholder: "40"  },
  DKK: { kmHigh: 50, kmLow: 1,   kmTypicalMin: 10, kmTypicalMax: 50, mileHigh: 75,  mileLow: 2,   mileTypicalMin: 15, mileTypicalMax: 75,  kmPlaceholder: "10",   milePlaceholder: "15",   minFeePlaceholder: "150" },
  SEK: { kmHigh: 75, kmLow: 1,   kmTypicalMin: 10, kmTypicalMax: 75, mileHigh: 100, mileLow: 2,   mileTypicalMin: 15, mileTypicalMax: 100, kmPlaceholder: "15",   milePlaceholder: "20",   minFeePlaceholder: "200" },
  NOK: { kmHigh: 75, kmLow: 1,   kmTypicalMin: 10, kmTypicalMax: 75, mileHigh: 100, mileLow: 2,   mileTypicalMin: 15, mileTypicalMax: 100, kmPlaceholder: "15",   milePlaceholder: "20",   minFeePlaceholder: "200" },
  CHF: { kmHigh: 8,  kmLow: 0.1, kmTypicalMin: 1,  kmTypicalMax: 8,  mileHigh: 12,  mileLow: 0.1, mileTypicalMin: 1,  mileTypicalMax: 12,  kmPlaceholder: "2",    milePlaceholder: "3",    minFeePlaceholder: "25"  },
  ZAR: { kmHigh: 30, kmLow: 0.5, kmTypicalMin: 5,  kmTypicalMax: 30, mileHigh: 45,  mileLow: 1,   mileTypicalMin: 8,  mileTypicalMax: 45,  kmPlaceholder: "8",    milePlaceholder: "12",   minFeePlaceholder: "100" },
  AED: { kmHigh: 20, kmLow: 0.5, kmTypicalMin: 3,  kmTypicalMax: 20, mileHigh: 30,  mileLow: 1,   mileTypicalMin: 5,  mileTypicalMax: 30,  kmPlaceholder: "5",    milePlaceholder: "8",    minFeePlaceholder: "50"  },
};

const DEFAULT_DELIVERY_FEE_THRESHOLDS: DeliveryFeeThresholds = {
  kmHigh: 5, kmLow: 0.1, kmTypicalMin: 1, kmTypicalMax: 5, mileHigh: 8, mileLow: 0.1, mileTypicalMin: 1, mileTypicalMax: 8,
  kmPlaceholder: "2.00", milePlaceholder: "3.00", minFeePlaceholder: "25",
};

/**
 * Returns currency-aware delivery fee thresholds and input placeholders for
 * the given country code. Use the returned `kmHigh`/`kmLow`/`mileHigh`/
 * `mileLow` values for "seems high/low" warnings, and the `*Placeholder`
 * strings directly as `<Input placeholder={…}>` props.
 */
export function getDeliveryFeeThresholds(countryCode: string | null | undefined): DeliveryFeeThresholds {
  const currency = countryCode ? COUNTRY_TO_CURRENCY[countryCode] : null;
  if (!currency) return DEFAULT_DELIVERY_FEE_THRESHOLDS;
  return DELIVERY_FEE_THRESHOLDS_BY_CURRENCY[currency] ?? DEFAULT_DELIVERY_FEE_THRESHOLDS;
}

/**
 * Currency-aware input placeholders for the listing wizard's rental daily
 * minimum price fields (self-drive daily rate and event daily rate).
 * The historic hardcoded examples were GBP-sized; Scandinavian and ZAR
 * currencies are ~7–18× GBP nominal, so a "50" example there reads as
 * absurdly cheap. Values are rough, round, market-typical daily rates —
 * they are examples only and drive no validation.
 */
export interface RentalMinimumPlaceholders {
  selfDriveDaily: string;
  eventDaily: string;
}

const RENTAL_MINIMUM_PLACEHOLDERS_BY_CURRENCY: Readonly<Record<string, RentalMinimumPlaceholders>> = {
  USD: { selfDriveDaily: "250",  eventDaily: "250"  },
  GBP: { selfDriveDaily: "250",  eventDaily: "250"  },
  EUR: { selfDriveDaily: "250",  eventDaily: "250"  },
  CHF: { selfDriveDaily: "280",  eventDaily: "280"  },
  CAD: { selfDriveDaily: "350",  eventDaily: "350"  },
  AUD: { selfDriveDaily: "400",  eventDaily: "400"  },
  NZD: { selfDriveDaily: "1000", eventDaily: "1000" },
  DKK: { selfDriveDaily: "2500", eventDaily: "2500" },
  SEK: { selfDriveDaily: "3500", eventDaily: "3500" },
  NOK: { selfDriveDaily: "3500", eventDaily: "3500" },
  ZAR: { selfDriveDaily: "6000", eventDaily: "6000" },
  AED: { selfDriveDaily: "900",  eventDaily: "900"  },
  PLN: { selfDriveDaily: "1000", eventDaily: "1000" },
};

const DEFAULT_RENTAL_MINIMUM_PLACEHOLDERS: RentalMinimumPlaceholders = {
  selfDriveDaily: "250",
  eventDaily: "250",
};

/**
 * Returns currency-aware placeholder strings for the listing wizard's
 * self-drive and event daily price inputs, keyed off the listing country
 * (same COUNTRY_TO_CURRENCY mapping as {@link getDeliveryFeeThresholds}).
 * Values mirror the per-currency default daily anchors so the example a
 * host sees is a realistic amount in their market. Use the strings
 * directly as `<Input placeholder={…}>` props.
 */
export function getRentalMinimumPlaceholders(countryCode: string | null | undefined): RentalMinimumPlaceholders {
  const currency = countryCode ? COUNTRY_TO_CURRENCY[countryCode] : null;
  if (!currency) return DEFAULT_RENTAL_MINIMUM_PLACEHOLDERS;
  return RENTAL_MINIMUM_PLACEHOLDERS_BY_CURRENCY[currency] ?? DEFAULT_RENTAL_MINIMUM_PLACEHOLDERS;
}

function chauffeuredHourlyFloorFor(countryCode: string | null | undefined): number {
  const code = countryCode ? COUNTRY_TO_CURRENCY[countryCode] : null;
  if (!code) return DEFAULT_CHAUFFEURED_HOURLY_FLOOR;
  return CHAUFFEURED_HOURLY_FLOOR_BY_CURRENCY[code] ?? DEFAULT_CHAUFFEURED_HOURLY_FLOOR;
}

/**
 * Per-currency labour-rate anchor for the **recommended** chauffeur hourly
 * (task #1770). Driver labour dominates the cost of a chauffeur ride, so
 * the legacy `recommendedDaily / 8` heuristic systematically under-shot
 * what hosts actually charge — it under-counted hourly rate by 2-3× in
 * the dev dataset. The anchor is the dev-DB median
 * `chauffeured_hourly_rate` per currency (n≥3); thinner currencies
 * (CHF, AED) use a EUR-style proxy. Final recommendation is
 * `max(anchor, recommendedDaily / 8)` so high-value vehicles still get a
 * vehicle-derived bump on top of the labour anchor.
 */
const CHAUFFEURED_HOURLY_ANCHOR_BY_CURRENCY: Readonly<Record<string, number>> = {
  USD: 80, GBP: 120, EUR: 130, CAD: 250, AUD: 150, NZD: 300, CHF: 130,
  SEK: 750, NOK: 1750, DKK: 1500, AED: 250, ZAR: 1750, PLN: 400,
};

const DEFAULT_CHAUFFEURED_HOURLY_ANCHOR = 80;

function chauffeuredHourlyAnchorFor(countryCode: string | null | undefined): number {
  const code = countryCode ? COUNTRY_TO_CURRENCY[countryCode] : null;
  if (!code) return DEFAULT_CHAUFFEURED_HOURLY_ANCHOR;
  return CHAUFFEURED_HOURLY_ANCHOR_BY_CURRENCY[code] ?? DEFAULT_CHAUFFEURED_HOURLY_ANCHOR;
}

/**
 * Per-currency fallback for the recommended self-drive daily when neither
 * comparables nor a valuation are available (task #1770). The legacy single
 * `250` constant was implicitly USD-sized and produced absurdly low
 * ceilings in Scandinavian / ZAR currencies (DKK 250 ≈ USD 35), causing
 * the event guardrail to flag essentially every IV=0 listing in those
 * markets. Anchored to the median self-drive daily observed per currency.
 */
const DEFAULT_DAILY_BY_CURRENCY: Readonly<Record<string, number>> = {
  USD: 250, GBP: 250, EUR: 250, CAD: 350, AUD: 400, NZD: 1000, CHF: 280,
  SEK: 3500, NOK: 3500, DKK: 2500, ZAR: 6000, AED: 900, PLN: 1000,
};

const DEFAULT_DAILY_FALLBACK = 250;

/**
 * Market-rate floor factor (task #4615 step 3). When close asking
 * comparables exist, the recommended daily rate must not sit further than
 * this fraction below the segment's asking median. A low valuation anchor
 * can otherwise drag the blend well under what comparable hosts actually
 * charge. Deliberately conservative: it only lifts clearly under-anchored
 * suggestions — cars with strong asking/realized evidence already sit at or
 * above `0.85 × asking-median`, so the floor is a no-op for them. (Realized
 * booking prices are already weighted into the base rate before this floor,
 * so they're intentionally not re-applied here.)
 */
const MARKET_RATE_FLOOR_FACTOR = 0.85;

function defaultDailyFor(countryCode: string | null | undefined): number {
  const code = countryCode ? COUNTRY_TO_CURRENCY[countryCode] : null;
  if (!code) return DEFAULT_DAILY_FALLBACK;
  return DEFAULT_DAILY_BY_CURRENCY[code] ?? DEFAULT_DAILY_FALLBACK;
}

/**
 * Per-currency scale factor used to translate `insured_value` into a
 * USD-equivalent bucket before applying the sliding-scale brackets
 * (task #2441). Roughly "units of local currency per 1 USD" — picked from
 * mid-2024 FX with conservative rounding so the brackets are stable across
 * normal FX drift. The result is the original USD-shaped brackets
 * (>20k / >50k / >100k / >200k → 0.005 / 0.004 / 0.003 / 0.002, base 0.007)
 * applied in every supported currency, so e.g. a 200,000 SEK car maps to
 * the same ~20k-USD-equivalent bucket as a 20,000 USD car instead of
 * landing in the >100k bucket because of the currency-naive thresholds.
 *
 * New currencies are mechanical to add: pick the local-per-USD ratio and
 * the existing brackets do the rest. Both the bucket boundaries and the
 * upper safety cap scale with this factor.
 */
const VALUATION_ANCHOR_SCALE_BY_CURRENCY: Readonly<Record<string, number>> = {
  // USD-purchasing-power-equivalent currencies: scale = 1 keeps the
  // existing brackets and produces the exact same numbers they do today
  // for valuation-only listings. GBP/EUR/CHF are within ~10–20% of USD;
  // CAD/AUD/NZD over-state value somewhat but their typical insured-value
  // ranges still land in the same brackets, so the recommendation is the
  // same to the nearest 25-step rounding.
  USD: 1,
  GBP: 1,
  EUR: 1,
  CHF: 1,
  CAD: 1,
  AUD: 1,
  // NZD scale = 1.6 reflects the ~NZDUSD exchange rate; the original scale = 1
  // misclassified cars insured at realistic NZD market value into the 0.005
  // bracket instead of the correct 0.007 bracket (e.g. 26,295 NZD ≈ USD 16,434).
  NZD: 1.6,
  // Currencies whose units-per-USD ratio is far enough from 1.0 that the
  // USD-shaped brackets misclassify normal cars (e.g. a 20,000 SEK car
  // is ~USD 1,900 — the smallest bracket — but the legacy code put it
  // in the 0.005 bracket as if it were a 20,000 USD car).
  AED: 3.7,
  DKK: 7,
  PLN: 4,
  SEK: 10,
  NOK: 10,
  ZAR: 18,
};

const DEFAULT_VALUATION_ANCHOR_SCALE = 1;

function valuationAnchorScaleFor(countryCode: string | null | undefined): number {
  const code = countryCode ? COUNTRY_TO_CURRENCY[countryCode] : null;
  if (!code) return DEFAULT_VALUATION_ANCHOR_SCALE;
  return VALUATION_ANCHOR_SCALE_BY_CURRENCY[code] ?? DEFAULT_VALUATION_ANCHOR_SCALE;
}

/**
 * Currency-aware floor and ceiling for the `recommendedDaily` clamp.
 * Scales the USD-shaped bounds (floor 25, ceiling 5000) by the per-currency
 * scale factor so high-magnitude currencies (ZAR ×18, SEK/NOK ×10, DKK ×7,
 * AED ×3.7) are not silently capped at a USD-sized number. For scale=1
 * currencies (USD/GBP/EUR/CAD/AUD/NZD/CHF) the output is identical to
 * today: `{ min: 25, max: 5000 }`.
 *
 * ZAR example: scale=18 → `{ min: 450, max: 90000 }`.
 */
function recommendedDailyClampFor(countryCode: string | null | undefined): { min: number; max: number } {
  const scale = valuationAnchorScaleFor(countryCode);
  return { min: 25 * scale, max: 5000 * scale };
}

/**
 * Sliding-scale daily rate used when valuation is the only signal.
 * Currency-aware (task #2441): `estimatedValue` is divided by the
 * per-currency scale to pick the bracket, so the USD-shaped brackets
 * apply consistently in every supported currency. The upper safety cap
 * scales with the same factor so a typo'd `insured_value` still gets
 * clipped but a legitimately expensive SEK/NOK/ZAR car isn't artificially
 * pinned down to a USD-sized ceiling.
 */
function valuationAnchorDaily(
  estimatedValue: number,
  countryCode?: string | null,
): number {
  const scale = valuationAnchorScaleFor(countryCode);
  const usdEquiv = estimatedValue / scale;
  const dailyRatePct = usdEquiv > 200000 ? 0.002 :
                       usdEquiv > 100000 ? 0.003 :
                       usdEquiv > 50000  ? 0.004 :
                       usdEquiv > 20000  ? 0.005 :
                       0.007;
  const raw = estimatedValue * dailyRatePct;
  const rounded = Math.round(raw / 25) * 25;
  // No 75-floor clamp here — the lower clamp lives in the unified function
  // so the "Low == Recommended" visual bug from the £75 floor can no
  // longer occur. The upper safety cap still stops a typo'd
  // `insured_value` (e.g. 10× the real value) from producing an unbounded
  // recommendation, but scales with currency so high-value non-USD cars
  // aren't artificially capped at a USD-sized number.
  const upperCap = 5000 * scale;
  return Math.min(Math.max(rounded, 0), upperCap);
}

/**
 * Filter realized booking comparables to the segment matching the listing's
 * currency, then drop outliers via an IQR clip. Pure helper exported only
 * for unit testing — `computeSuggestedPricing` calls it directly.
 *
 * Currency segmentation: when `targetCurrency` is set, only rows whose
 * currency matches feed the median (so a UK listing never blends DKK
 * bookings into its GBP recommendation). When `targetCurrency` is null
 * (unknown country) we fall back to the largest single-currency segment so
 * we still produce a meaningful median rather than mixing buckets.
 *
 * Outlier clipping: 1.5×IQR around Q1/Q3 — the standard Tukey fence —
 * applied only when there are ≥5 samples (below that the IQR isn't
 * meaningful). Returns the clipped values sorted ascending.
 */
export function filterAndClipBookingRates(
  bookings: readonly BookingComparable[] | undefined,
  targetCurrency: string | null,
): number[] {
  if (!bookings || bookings.length === 0) return [];
  const usable = bookings
    .filter((b) => b && Number.isFinite(b.dailyRate) && b.dailyRate > 0 && typeof b.currency === "string");
  if (usable.length === 0) return [];

  let segment: BookingComparable[];
  if (targetCurrency) {
    segment = usable.filter((b) => b.currency.toUpperCase() === targetCurrency.toUpperCase());
  } else {
    // Pick the largest single-currency bucket so we never mix segments.
    const buckets = new Map<string, BookingComparable[]>();
    for (const b of usable) {
      const key = b.currency.toUpperCase();
      const bucket = buckets.get(key);
      if (bucket) bucket.push(b);
      else buckets.set(key, [b]);
    }
    let largest: BookingComparable[] = [];
    for (const bucket of buckets.values()) {
      if (bucket.length > largest.length) largest = bucket;
    }
    segment = largest;
  }

  const sorted = segment.map((b) => b.dailyRate).sort((a, b) => a - b);
  if (sorted.length < 5) return sorted;

  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const iqr = q3 - q1;
  const lo = q1 - 1.5 * iqr;
  const hi = q3 + 1.5 * iqr;
  return sorted.filter((v) => v >= lo && v <= hi);
}

const PREMIUM_CITIES = [
  "los angeles", "miami", "new york", "san francisco",
  "london", "paris", "monaco", "dubai", "sydney",
];

/**
 * **The single pricing calculation.** Replaces both the AI-enrichment route's
 * comparables-based blend and the legacy `computePriceBounds()` valuation
 * formula. Returns the recommended daily price (the only number a host sees)
 * plus internal floors and ceilings used for validation and the admin
 * above-ceiling flag.
 *
 * Blend rules:
 * - Both comparables and valuation present → 60% comparables median + 40%
 *   valuation anchor; confidence "high".
 * - Comparables only → comparables median; confidence "medium".
 * - Valuation only → valuation anchor; confidence "low" (task #2441 — without
 *   any market evidence, the recommendation is a defensible heuristic and the
 *   badge should signal that to the host).
 * - Neither → per-currency default daily (task #1770); confidence "low".
 *
 * The legacy 75 hard clamp is gone — the recommendation can fall below 75
 * when comparables genuinely do, so the "Low equals Recommended" visual
 * bug can no longer happen. The chauffeured hourly floor uses the
 * per-currency table folded in from Task #1477.
 *
 * **Per-rental-type recommendations & bounds (recalibrated in task #1770
 * against the dev DB, n=717):**
 * - Self-drive: recommended = blended baseRate × adjustments. Floor 0.5×,
 *   ceiling 3×. Pass rate ~85% (unchanged — already calibrated).
 * - Event: recommended = recommendedDaily × **1.0** (was 1.5; the observed
 *   event/self-drive median ratio is 1.0, p90 1.5). Floor 0.33× of
 *   recommendedDaily (long lower tail of legitimately low-priced US event
 *   cars), ceiling 6× of recommendedDailyEvent (was 3×; events have a long
 *   premium-pricing tail). Pass rate lifted from 72% → 92%.
 * - Chauffeur: recommended = `max(per-currency labour anchor,
 *   recommendedDaily / 8)`. Ceiling 5× recommendedHourlyChauffeured (was
 *   3×). The per-currency hard floor is unchanged (task #1477) and remains
 *   the only host-facing chauffeur error. Pass rate lifted from 47% → 94%.
 *
 * The full per-currency before/after report lives in
 * `.local/tasks/_artifacts/guardrail-recalibration-report.md`.
 */
export function computeSuggestedPricing(input: SuggestedPricingInput): SuggestedPricing {
  const factors: PricingFactor[] = [];
  const make = (input.make || "").trim();

  const compsRaw = (input.comparables || []).filter((p) => Number.isFinite(p) && p > 0);
  const comps = [...compsRaw].sort((a, b) => a - b);
  const comparableCount = comps.length;

  let platformMedian = 0;
  let hasComps = false;
  if (comps.length >= 3) {
    hasComps = true;
    const midIdx = Math.floor(comps.length / 2);
    platformMedian = comps.length % 2 === 0
      ? Math.round((comps[midIdx - 1] + comps[midIdx]) / 2)
      : comps[midIdx];
    factors.push({
      factor: "Platform Comparables",
      impact: "primary",
      detail: `${comps.length} similar ${make || "car"} listings, median ${platformMedian}`,
    });
  }

  const insuredValue = input.insuredValue && input.insuredValue > 0 ? Number(input.insuredValue) : 0;
  let auctionRate = 0;
  if (insuredValue > 0) {
    auctionRate = valuationAnchorDaily(insuredValue, input.countryCode);
    factors.push({
      factor: "Market Valuation",
      impact: hasComps ? "secondary" : "primary",
      detail: `Estimated value ${insuredValue.toLocaleString()}, base rate ${auctionRate}`,
    });
  }

  const completedBookings = Math.max(0, Number(input.completedBookings) || 0);
  let bookingDemandAdj = 0;
  if (completedBookings >= 10) {
    bookingDemandAdj = 0.12;
    factors.push({ factor: "High Demand", impact: "positive", detail: `${completedBookings} successful bookings` });
  } else if (completedBookings >= 5) {
    bookingDemandAdj = 0.07;
    factors.push({ factor: "Moderate Demand", impact: "positive", detail: `${completedBookings} successful bookings` });
  } else if (completedBookings >= 1) {
    bookingDemandAdj = 0.03;
  }

  const apexScore = Math.max(0, Number(input.apexScore) || 0);
  let apexAdj = 0;
  if (apexScore >= 90) {
    apexAdj = 0.15;
    factors.push({ factor: "Elite APEX Score", impact: "positive", detail: `Score ${apexScore}/100` });
  } else if (apexScore >= 75) {
    apexAdj = 0.08;
    factors.push({ factor: "Strong APEX Score", impact: "positive", detail: `Score ${apexScore}/100` });
  } else if (apexScore >= 60) {
    apexAdj = 0.03;
  } else if (apexScore > 0 && apexScore < 40) {
    apexAdj = -0.05;
    factors.push({ factor: "Low APEX Score", impact: "negative", detail: `Score ${apexScore}/100` });
  }

  const condition = Number(input.condition) || 0;
  let conditionAdj = 0;
  if (condition === 1) {
    conditionAdj = 0.10;
    factors.push({ factor: "Excellent Condition", impact: "positive", detail: "Top condition commands a premium" });
  } else if (condition === 2) {
    conditionAdj = 0.04;
  } else if (condition === 4) {
    conditionAdj = -0.08;
    factors.push({ factor: "Fair Condition", impact: "negative", detail: "Condition may limit demand" });
  }

  const cityLower = (input.city || "").toLowerCase();
  let locationAdj = 0;
  if (PREMIUM_CITIES.some((pc) => cityLower.includes(pc))) {
    locationAdj = 0.12;
    factors.push({ factor: "Premium Location", impact: "positive", detail: `${input.city} is a high-demand market` });
  }

  // Realized booking comparables (task #1590). Filter to the segment
  // matching the listing's currency so we never blend DKK with USD, drop
  // outliers via an IQR clip so a single mispriced legacy booking can't
  // dominate the median, then take the median if ≥3 usable samples remain.
  const targetCurrency = input.countryCode ? COUNTRY_TO_CURRENCY[input.countryCode] : null;
  const bookingRates = filterAndClipBookingRates(input.bookingComparables, targetCurrency);
  const realizedBookingCount = bookingRates.length;
  let realizedMedian: number | null = null;
  if (realizedBookingCount >= 3) {
    const midIdx = Math.floor(realizedBookingCount / 2);
    realizedMedian = realizedBookingCount % 2 === 0
      ? Math.round((bookingRates[midIdx - 1] + bookingRates[midIdx]) / 2)
      : Math.round(bookingRates[midIdx]);
  }

  // Sample-size-aware weight: 3 samples ≈ 7%, 30+ samples ≈ 70%. Caps at
  // 0.7 so asking-price + valuation always retain at least 30% of the
  // blend — realized prices are a strong signal but we don't want a
  // narrow segment of recent bookings to overpower the broader market.
  const realizedWeight = realizedMedian != null
    ? Math.min(0.7, (realizedBookingCount / 30) * 0.7)
    : 0;

  let baseRate: number;
  let confidence: "high" | "medium" | "low";
  if (realizedMedian != null) {
    // Realized signal present — blend it with whatever asking/valuation
    // signals exist. The remaining weight splits 60/40 asking vs.
    // valuation when both are present, otherwise falls entirely on the
    // one that is.
    const remaining = 1 - realizedWeight;
    let askingWeight = 0;
    let valuationWeight = 0;
    if (hasComps && auctionRate > 0) {
      askingWeight = remaining * 0.6;
      valuationWeight = remaining * 0.4;
    } else if (hasComps) {
      askingWeight = remaining;
    } else if (auctionRate > 0) {
      valuationWeight = remaining;
    } else {
      // Realized is the only signal — let it carry the entire base rate.
      askingWeight = 0;
      valuationWeight = 0;
    }
    const totalWeight = realizedWeight + askingWeight + valuationWeight;
    baseRate = Math.round(
      (realizedMedian * realizedWeight
        + platformMedian * askingWeight
        + auctionRate * valuationWeight)
      / (totalWeight || 1),
    );
    // Confidence: ≥10 realized samples + any other signal → high.
    // Realized only with ≥3 samples → medium. Otherwise inherits the
    // existing rules below.
    if (realizedBookingCount >= 10 && (hasComps || auctionRate > 0)) {
      confidence = "high";
    } else if (realizedBookingCount >= 10) {
      confidence = "medium";
    } else if (hasComps && auctionRate > 0) {
      confidence = "high";
    } else {
      confidence = "medium";
    }
    factors.push({
      factor: "Realized Booking Prices",
      impact: realizedBookingCount >= 10 ? "primary" : "secondary",
      detail: `${realizedBookingCount} past booking${realizedBookingCount === 1 ? "" : "s"} of similar cars, median ${realizedMedian}`,
    });
  } else if (hasComps && auctionRate > 0) {
    baseRate = Math.round(platformMedian * 0.6 + auctionRate * 0.4);
    confidence = "high";
  } else if (hasComps) {
    baseRate = platformMedian;
    confidence = "medium";
  } else if (auctionRate > 0) {
    baseRate = auctionRate;
    // Task #2441: valuation-only (no comparables, no realized bookings) is
    // a defensible heuristic, not market evidence. Report "low" so the
    // host-facing badge accurately reflects the reduced confidence.
    confidence = "low";
  } else if (input.segmentMedianDaily && input.segmentMedianDaily > 0) {
    // Task #4615 step 4 — segment-aware classic-car fallback. With no
    // insured value, no close comparables and no realized bookings, a
    // broader same-make / same-era market median (computed by the API
    // loader from a wider query than the close-comparables set) is a far
    // better anchor than a flat per-currency default. The per-currency
    // default remains the last resort below when even the broad query is
    // too thin to yield a median.
    baseRate = Math.round(input.segmentMedianDaily);
    confidence = "low";
    factors.push({
      factor: "Segment Market Median",
      impact: "primary",
      detail: `Broader ${make || "segment"} market median ${baseRate}`,
    });
  } else {
    baseRate = defaultDailyFor(input.countryCode);
    confidence = "low";
    factors.push({ factor: "Default Rate", impact: "neutral", detail: "Insufficient data — using category default" });
  }

  const totalAdj = 1 + apexAdj + conditionAdj + locationAdj + bookingDemandAdj;
  // Task #4615 step 3 — market-rate floor. Don't let the recommendation
  // sit far below the segment's asking median when close comparables
  // exist. Applied *inside* the currency-aware clamp so the scaled ceiling
  // still wins and a high asking median can't push the rate past the cap.
  const adjustedBase = baseRate * totalAdj;
  const marketRateFloor = hasComps && platformMedian > 0
    ? platformMedian * MARKET_RATE_FLOOR_FACTOR
    : 0;
  const flooredBase = Math.max(adjustedBase, marketRateFloor);
  const clamp = recommendedDailyClampFor(input.countryCode);
  const recommendedDaily = Math.max(clamp.min, Math.min(clamp.max, Math.round(flooredBase / 25) * 25));

  // Per-rental-type derived recommendations (task #1770).
  // Event recommendation now uses 1.0× (was 1.5×) — dev-DB median
  // event/self-drive ratio is 1.0; most hosts price events at, not 1.5×
  // above, their self-drive rate.
  const recommendedDailyEvent = Math.round((recommendedDaily * 1.0) / 25) * 25;
  // Chauffeur recommendation now floors on the per-currency labour anchor
  // (was the per-currency hard floor). The legacy `recommendedDaily/8`
  // path under-counts hourly rate by 2-3× because driver labour, not
  // vehicle value, drives chauffeur pricing. The /8 term still applies
  // for high-value cars where it exceeds the labour anchor.
  const recommendedHourlyChauffeured = Math.max(
    chauffeuredHourlyAnchorFor(input.countryCode),
    Math.round((recommendedDaily / 8) / 5) * 5,
  );

  // Floors. Self-drive and event floors are internal-only (no host-facing
  // error). The chauffeured floor is the **only** hard minimum and uses
  // the per-currency table — unchanged from task #1477 by design.
  const minDailySelfDrive = Math.max(25, Math.round((recommendedDaily * 0.5) / 5) * 5);
  // Event floor anchored to 0.33× recommendedDaily (NOT recommendedDailyEvent).
  // The dev-DB p25 of event/self-drive is 0.77, but a long lower tail of
  // hosts (especially USD; ~16% of US event listings priced below 0.5×) needs
  // a 0.33× band to capture without flagging legitimate low-end pricing.
  // Anchoring on recommendedDaily (not recommendedDailyEvent) keeps the band
  // stable when the event multiplier shifts.
  const minDailyEvent = Math.max(25, Math.round((recommendedDaily * 0.33) / 5) * 5);
  const minHourlyChauffeured = chauffeuredHourlyFloorFor(input.countryCode);

  // Ceilings — used by the above-ceiling admin flag only.
  // Self-drive 3× kept (already at 85% pass). Event raised to 6× and
  // chauffeur raised to 5× — both segments have a long premium-pricing
  // tail (luxury chauffeur fleets, wedding-event cars) that 3× was
  // clipping.
  // Floor each ceiling at the default-case ceiling for the currency, so that a
  // listing with low/no valuation data is never blocked at a tighter threshold
  // than a listing with no valuation data at all.  Invariant: ceiling >= default.
  const defaultDailyFloor = defaultDailyFor(input.countryCode);
  const maxDailySelfDrive = Math.max(
    Math.round((defaultDailyFloor * 3) / 25) * 25,
    Math.round((recommendedDaily * 3) / 25) * 25,
  );
  const maxDailyEvent = Math.max(
    Math.round((defaultDailyFloor * 6) / 25) * 25,
    Math.round((recommendedDailyEvent * 6) / 25) * 25,
  );
  const maxHourlyChauffeured = Math.max(
    minHourlyChauffeured,
    Math.round((recommendedHourlyChauffeured * 5) / 5) * 5,
  );

  return {
    recommendedDaily,
    recommendedDailyEvent,
    recommendedHourlyChauffeured,
    minDailySelfDrive,
    minDailyEvent,
    minHourlyChauffeured,
    maxDailySelfDrive,
    maxDailyEvent,
    maxHourlyChauffeured,
    confidence,
    factors,
    comparableCount,
    bookingCount: completedBookings,
    realizedBookingCount,
    realizedMedian,
    apexScore,
  };
}

/**
 * @deprecated Thin shim around {@link computeSuggestedPricing}. Use the
 * unified function directly — it returns the same numbers plus per-rental-
 * type recommendations and confidence metadata. Kept so external consumers
 * (mobile API contracts, etc.) continue to compile while we migrate call
 * sites away from the legacy `{ dailyMin, dailyMax, hourlyMin, hourlyMax }`
 * shape.
 */
export function computePriceBounds(
  insuredValue: number,
  countryCode: string | null = "US",
): PriceBounds {
  const r = computeSuggestedPricing({ insuredValue, countryCode });
  return {
    dailyMin: r.minDailySelfDrive,
    dailyMax: r.maxDailySelfDrive,
    hourlyMin: r.minHourlyChauffeured,
    hourlyMax: r.maxHourlyChauffeured,
  };
}

/**
 * How a host-facing suggestion should be displayed for one rental type
 * (task #4615 step 1):
 *  - `single` — show the computed suggestion as-is.
 *  - `range`  — show a market band `low–high` where the host's own current
 *               price is the upper bound. Used when the computed guide is
 *               *below* what the host already charges, so we never nudge a
 *               host to lower their price; we frame their rate as the top of
 *               a healthy market band instead.
 */
export type SuggestionDisplay =
  | { mode: "single"; value: number }
  | { mode: "range"; low: number; high: number };

/**
 * Resolve how to display a computed suggestion against the host's current
 * price for the same rental type (task #4615).
 *
 * Rules:
 *  - No host price set (null / ≤ 0, e.g. a brand-new listing) → show the
 *    single suggestion.
 *  - Suggestion ≥ host price → show the single suggestion (it nudges up,
 *    which is the behaviour we want).
 *  - Suggestion < host price → show a band `[suggestion, hostPrice]` with
 *    the host's price as the upper bound. Never frame this as "lower your
 *    price".
 */
export function resolveSuggestionDisplay(
  suggestion: number,
  hostPrice: number | null | undefined,
): SuggestionDisplay {
  const host =
    typeof hostPrice === "number" && Number.isFinite(hostPrice) && hostPrice > 0
      ? hostPrice
      : null;
  if (host == null || suggestion >= host) {
    return { mode: "single", value: suggestion };
  }
  return { mode: "range", low: suggestion, high: host };
}
